/*
 * opk-question-flow-persistence.js
 * ---------------------------------
 * Makes the multi-step question flow (opk-question-flow-section) reload-proof.
 *
 * The main flow script keeps every answer ONLY in transient DOM state
 * (checked radios/checkboxes + input/textarea values) while it persists the
 * user's position in the URL (?step=N). So any full page reload part-way
 * through — very common on mobile Safari, which reloads backgrounded tabs —
 * re-renders the page at the saved late step with every answer blank, yet the
 * user can still reach the submit step and POST an empty questionnaire that
 * still creates a customer (name only, no answers).
 *
 * This module fixes that by mirroring the answers into sessionStorage as the
 * user goes, and rehydrating the DOM on load before the user can reach submit.
 *
 * Design notes:
 *  - Storage is namespaced per flow (pathname) and guarded by a "signature"
 *    (ordered list of metafield names). If the merchant restructures the flow,
 *    stale saved answers are discarded instead of applied to the wrong steps.
 *  - Restore writes .checked/.value DIRECTLY (no per-input change events) so it
 *    never trips the main script's "unique option" clearing logic across steps.
 *    It then dispatches ONE `input` event on the active step so the main
 *    script re-evaluates the Next button. `input` (not `change`) is used on
 *    purpose: the main script's updateNext listens to input+change, but its
 *    unique/additional-field handler listens to change only — so `input`
 *    refreshes the button without re-running that logic.
 *  - sessionStorage (not localStorage): scoped to the tab, survives reload,
 *    gone when the tab closes. Cleared on successful submit.
 */
(function () {
  "use strict";

  var FORM_SELECTOR = "#step-form";
  var STEP_SELECTOR = ".question-page";
  var OPTION_SELECTOR = "[data-question-option] input";
  var TEXT_SELECTOR = "[data-question-input], [data-contact-input]";
  var TEXTAREA_SELECTOR = ".question-option-additional textarea";
  var SAVE_DEBOUNCE_MS = 150;

  var form = document.querySelector(FORM_SELECTOR);
  if (!form) return;

  var steps = [].slice.call(form.querySelectorAll(STEP_SELECTOR));
  if (!steps.length) return;

  var storageKey = "md_quiz_answers::" + location.pathname;

  // --- safe storage helpers (private mode / quota can throw) ----------------
  function readStore() {
    try {
      var raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function writeStore(obj) {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(obj));
    } catch (e) {
      /* ignore */
    }
  }
  function clearStore() {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (e) {
      /* ignore */
    }
  }

  // Signature of the current flow structure. If this changes, saved answers
  // from a different flow version are not applied.
  function signature() {
    return steps
      .map(function (s) {
        return s.getAttribute("data-metafield-name") || "";
      })
      .join("|");
  }

  function stepKey(step, index) {
    return step.getAttribute("data-metafield-name") || "step-" + index;
  }

  // --- capture --------------------------------------------------------------
  function captureStep(step) {
    var checked = [];
    [].forEach.call(step.querySelectorAll(OPTION_SELECTOR), function (input) {
      if (input.checked) checked.push(input.value);
    });

    // Text-ish values keyed by "type#ordinalWithinType" so they stay stable
    // across reloads regardless of how the different field types are ordered.
    var values = {};
    var typeCounts = {};
    [].forEach.call(step.querySelectorAll(TEXT_SELECTOR), function (input) {
      var type = input.type || "text";
      var n = typeCounts[type] || 0;
      typeCounts[type] = n + 1;
      if (input.value.trim() !== "") values[type + "#" + n] = input.value;
    });
    [].forEach.call(step.querySelectorAll(TEXTAREA_SELECTOR), function (ta, idx) {
      if (ta.value.trim() !== "") values["textarea#" + idx] = ta.value;
    });

    return { c: checked, v: values };
  }

  function captureAll() {
    var data = { sig: signature(), steps: {} };
    steps.forEach(function (step, index) {
      var snap = captureStep(step);
      if (snap.c.length || Object.keys(snap.v).length) {
        data.steps[stepKey(step, index)] = snap;
      }
    });
    return data;
  }

  // --- restore --------------------------------------------------------------
  function restoreStep(step, snap) {
    if (!snap) return;

    [].forEach.call(step.querySelectorAll(OPTION_SELECTOR), function (input) {
      input.checked = snap.c.indexOf(input.value) !== -1;
    });

    var typeCounts = {};
    [].forEach.call(step.querySelectorAll(TEXT_SELECTOR), function (input) {
      var type = input.type || "text";
      var n = typeCounts[type] || 0;
      typeCounts[type] = n + 1;
      var key = type + "#" + n;
      if (Object.prototype.hasOwnProperty.call(snap.v, key)) input.value = snap.v[key];
    });
    [].forEach.call(step.querySelectorAll(TEXTAREA_SELECTOR), function (ta, idx) {
      var key = "textarea#" + idx;
      if (Object.prototype.hasOwnProperty.call(snap.v, key)) ta.value = snap.v[key];
    });

    // Reveal any additional field whose connected option is now checked
    // (mirrors the main script's reveal, done directly to avoid change events).
    var checkedConnects = {};
    [].forEach.call(step.querySelectorAll("[data-additional-field-option]"), function (opt) {
      if (opt.checked) {
        var c = (opt.dataset.additionalFieldOption || "").trim();
        if (c) checkedConnects[c] = true;
      }
    });
    [].forEach.call(step.querySelectorAll(".question-option-additional"), function (add) {
      var c = (add.dataset.additionalField || "").trim();
      if (c && checkedConnects[c]) add.classList.add("visible");
    });
  }

  function restoreAll() {
    var data = readStore();
    if (!data || !data.steps) return false;
    if (data.sig !== signature()) {
      clearStore(); // flow structure changed — don't apply stale answers
      return false;
    }

    var restoredAny = false;
    steps.forEach(function (step, index) {
      var snap = data.steps[stepKey(step, index)];
      if (snap) {
        restoreStep(step, snap);
        restoredAny = true;
      }
    });

    if (restoredAny) {
      // Nudge the main script to re-evaluate the Next button for the visible
      // step. `input` triggers updateNext but NOT the unique/reveal handler.
      var active = form.querySelector('.question-page[data-page="active"]') || steps[0];
      var probe =
        active &&
        active.querySelector(OPTION_SELECTOR + ", " + TEXT_SELECTOR + ", " + TEXTAREA_SELECTOR);
      if (probe) {
        probe.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
    return restoredAny;
  }

  // --- submit guard ---------------------------------------------------------
  // Persistence is best-effort: it only recovers answers when they were saved
  // in THIS tab's sessionStorage and it survived (reload within the same tab).
  // It cannot help a fresh tab, a tab the browser fully discarded, private
  // mode, or someone who lands deep in the flow via a ?step=N URL. In those
  // cases the DOM has no answers, yet the user can still reach and fire the
  // submit — creating a customer with a name but a blank questionnaire.
  //
  // This guard is the deterministic backstop: it mirrors the main script's own
  // "is this required step answered?" logic and refuses to let the submit fire
  // while any required question is still empty, bouncing the user back to the
  // first unanswered step instead. It is intentionally CONSERVATIVE — it only
  // blocks when a required step is genuinely empty, so a normally-completed
  // (sequentially validated) flow never trips it.

  // Mirrors the main script's updateNext() per-step check.
  function stepAnswered(step) {
    if (step.getAttribute("data-required") !== "true") return true;
    var hasOptions = !!step.querySelector("[data-question-option]");
    var optionOk = !hasOptions || !!step.querySelector("[data-question-option] input:checked");
    var contacts = step.querySelectorAll("[data-contact-input]");
    var contactsOk =
      contacts.length === 0 ||
      [].every.call(contacts, function (i) {
        return i.value.trim().length > 0;
      });
    var qi = step.querySelector("[data-question-input]");
    var qiOk = !qi || qi.value.trim().length > 0;
    return optionOk && contactsOk && qiOk;
  }

  function firstMissingRequiredIndex() {
    for (var i = 0; i < steps.length; i++) {
      if (!stepAnswered(steps[i])) return i;
    }
    return -1;
  }

  // In-page navigation to a step — mirrors the main script's step display so we
  // don't have to reload. Toggles data-page, updates the progress bar, syncs
  // the URL, resets the prev button, and pokes updateNext for the new step.
  function showStep(index) {
    steps.forEach(function (s, i) {
      s.dataset.page = i === index ? "active" : "";
    });
    var items = [].slice.call(document.querySelectorAll("[data-progress-item]"));
    items.forEach(function (it) {
      it.dataset.progressItem = "";
    });
    for (var p = 0; p <= index && p < items.length; p++) {
      items[p].dataset.progressItem = "active";
    }
    try {
      var params = new URLSearchParams(location.search);
      params.set("step", index);
      history.pushState({}, "", location.pathname + "?" + params.toString());
    } catch (e) {
      /* ignore */
    }
    var prev = document.querySelector("[data-prev-button]");
    if (prev) prev.disabled = index === 0;
    var active = steps[index];
    var probe =
      active &&
      active.querySelector(OPTION_SELECTOR + ", " + TEXT_SELECTOR + ", " + TEXTAREA_SELECTOR);
    if (probe) probe.dispatchEvent(new Event("input", { bubbles: true }));
    window.scrollTo(0, 0);
  }

  var NOTICE_ID = "md-quiz-incomplete-notice";
  function showIncompleteNotice(step) {
    if (!step || step.querySelector("#" + NOTICE_ID)) return;
    var note = document.createElement("div");
    note.id = NOTICE_ID;
    note.setAttribute("role", "alert");
    note.textContent =
      "Vul eerst alle vragen in voordat je de vragenlijst verstuurt.";
    note.style.cssText =
      "margin:0 0 16px;padding:12px 16px;border-radius:8px;background:#fdecec;" +
      "color:#b3261e;font-size:14px;line-height:1.4;border:1px solid #f3b7b3;";
    step.insertBefore(note, step.firstChild);
  }

  // --- wiring ---------------------------------------------------------------
  var saveTimer = null;
  function scheduleSave() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      saveTimer = null;
      writeStore(captureAll());
    }, SAVE_DEBOUNCE_MS);
  }

  function onReady(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  }

  onReady(function () {
    // Run after the main flow script's DOMContentLoaded handler has attached
    // its listeners and jumped to the URL step, so our restore + probe event
    // take effect on top of its initial state.
    setTimeout(restoreAll, 0);

    // Persist on every answer change.
    form.addEventListener("input", scheduleSave);
    form.addEventListener("change", scheduleSave);

    // Guard the submit. This listener is registered NOW (synchronously, during
    // this deferred script's execution) while the main script registers its
    // own click handler later, inside its DOMContentLoaded callback — so ours
    // runs first at the target and can stopImmediatePropagation() to block the
    // main submit. Capture phase is belt-and-suspenders for the same goal.
    var nextButton = document.querySelector("[data-next-button]");
    if (nextButton) {
      nextButton.addEventListener(
        "click",
        function (e) {
          // Only intervene on the actual submit step.
          if (!form.querySelector('[data-request="true"][data-page="active"]')) return;

          var missing;
          try {
            missing = firstMissingRequiredIndex();
          } catch (err) {
            // Fail OPEN: never block a real lead because of a guard bug.
            console.error("[Medlose] submit guard error:", err);
            return;
          }

          if (missing !== -1) {
            // Questionnaire is incomplete — block the empty submit and send the
            // user to the first unanswered required step.
            e.preventDefault();
            e.stopImmediatePropagation();
            console.warn("[Medlose] blocked empty submit; bounced to step", missing);
            showStep(missing);
            showIncompleteNotice(steps[missing]);
            return;
          }

          // Complete submit — let the main script POST, then drop the backup.
          setTimeout(clearStore, 0);
        },
        true
      );
    }
  });
})();
